/**
 * PreflightChecklist.tsx
 *
 * Hard-gate beta launch checklist. All items must be checked before
 * inviting beta teachers. Completion state is stored in localStorage
 * for the admin running the final review.
 */

import React, { useState, useCallback } from 'react';
import { CheckCircle2, Circle, AlertTriangle, ClipboardList, RotateCcw } from 'lucide-react';

interface ChecklistItem {
  id: string;
  label: string;
  group: string;
  critical?: boolean;
  note?: string;
}

const CHECKLIST: ChecklistItem[] = [
  // Auth & Sessions
  { id: 'auth-works',        group: 'Auth & Sessions',      label: 'Authentication works (sign-up, sign-in, sign-out)', critical: true },
  { id: 'session-recovery',  group: 'Auth & Sessions',      label: 'Session recovery works after expiry' },
  { id: 'user-isolation',    group: 'Auth & Sessions',      label: 'User isolation verified — teacher A cannot access teacher B data', critical: true },

  // Data
  { id: 'db-persistence',    group: 'Database',             label: 'Database writes persist across server restarts', critical: true },
  { id: 'backup-configured', group: 'Database',             label: 'Backups configured for production DB', critical: true },

  // Core features
  { id: 'lesson-gen',        group: 'Instructional Core',   label: 'Lesson generation verified (AI produces correct content)', critical: true },
  { id: 'activity-sync',     group: 'Instructional Core',   label: 'Activity Sheet sync verified (pulls from lesson blueprint)' },
  { id: 'presentation-sync', group: 'Instructional Core',   label: 'Presentation sync verified (slides match lesson)' },
  { id: 'assessment-align',  group: 'Instructional Core',   label: 'Assessment alignment verified (questions match lesson skill)' },

  // Exports
  { id: 'pptx-opens',        group: 'Exports',              label: 'PPTX downloads and opens correctly in PowerPoint / Google Slides' },
  { id: 'pdf-print',         group: 'Exports',              label: 'PDF / print output verified (no cut-off content)' },

  // Legal
  { id: 'privacy-published', group: 'Legal',                label: 'Privacy Policy published and accessible', critical: true, note: 'Requires attorney review before public launch.' },
  { id: 'terms-published',   group: 'Legal',                label: 'Terms of Service published and accessible', critical: true, note: 'Requires attorney review before public launch.' },

  // Reliability
  { id: 'feedback-working',  group: 'Reliability & Support', label: 'Help & Feedback system working (submissions stored in DB)' },
  { id: 'error-monitoring',  group: 'Reliability & Support', label: 'Error monitoring active (Sentry or equivalent)' },

  // Production
  { id: 'prod-tested',       group: 'Production Deploy',    label: 'Production deployment tested end-to-end', critical: true, note: 'Test on a real device, different network, not just Replit preview.' },

  // Launch materials
  { id: 'beta-materials',    group: 'Launch Readiness',     label: 'Beta invitation materials ready (email template, instructions, expectations)' },
];

const STORAGE_KEY = 'tb-preflight-checklist-v1';

function loadChecked(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function saveChecked(ids: Set<string>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
}

const GROUPS = [...new Set(CHECKLIST.map(i => i.group))];

export function PreflightChecklist() {
  const [checked, setChecked] = useState<Set<string>>(loadChecked);

  const toggle = useCallback((id: string) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      saveChecked(next);
      return next;
    });
  }, []);

  const total     = CHECKLIST.length;
  const done      = CHECKLIST.filter(i => checked.has(i.id)).length;
  const critical  = CHECKLIST.filter(i => i.critical);
  const critDone  = critical.filter(i => checked.has(i.id)).length;
  const allGreen  = done === total;
  const critGreen = critDone === critical.length;

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ClipboardList size={16} className="text-primary" />
            <p className="text-sm font-bold text-slate-900">Beta Launch Preflight Checklist</p>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            Every required item must be checked before inviting the first teachers.
            State is saved in this browser — run the final review from the same machine.
          </p>
        </div>
        <button
          onClick={() => { const empty = new Set<string>(); setChecked(empty); saveChecked(empty); }}
          className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
          title="Reset all checks"
        >
          <RotateCcw size={11} /> Reset
        </button>
      </div>

      {/* Progress bar */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>{done} / {total} items checked</span>
          <span className={critGreen ? 'text-emerald-600 font-semibold' : 'text-amber-600 font-semibold'}>
            Critical: {critDone} / {critical.length}
          </span>
        </div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${allGreen ? 'bg-emerald-500' : critGreen ? 'bg-primary' : 'bg-amber-400'}`}
            style={{ width: `${(done / total) * 100}%` }}
          />
        </div>
        {allGreen && (
          <p className="text-xs text-emerald-600 font-semibold flex items-center gap-1.5">
            <CheckCircle2 size={12} /> All items checked — ready for controlled private beta! 🎉
          </p>
        )}
        {!critGreen && (
          <p className="text-xs text-amber-700 flex items-center gap-1.5">
            <AlertTriangle size={12} /> {critical.length - critDone} critical item{critical.length - critDone !== 1 ? 's' : ''} remaining.
          </p>
        )}
      </div>

      {/* Items grouped */}
      {GROUPS.map(group => {
        const items = CHECKLIST.filter(i => i.group === group);
        return (
          <div key={group} className="space-y-1">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pb-1 border-b border-slate-100">
              {group}
            </p>
            {items.map(item => {
              const isChecked = checked.has(item.id);
              return (
                <label
                  key={item.id}
                  className={`flex items-start gap-3 py-2.5 px-3 rounded-xl cursor-pointer transition-colors
                    ${isChecked ? 'bg-emerald-50/60' : 'hover:bg-slate-50'}`}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={isChecked}
                    onChange={() => toggle(item.id)}
                  />
                  {isChecked ? (
                    <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <Circle size={16} className={`flex-shrink-0 mt-0.5 ${item.critical ? 'text-amber-400' : 'text-slate-300'}`} />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm leading-snug ${isChecked ? 'line-through text-slate-400' : item.critical ? 'font-medium text-slate-800' : 'text-slate-700'}`}>
                      {item.label}
                      {item.critical && !isChecked && (
                        <span className="ml-1.5 text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded px-1 py-0.5 no-underline">Required</span>
                      )}
                    </p>
                    {item.note && (
                      <p className="text-[10px] text-slate-400 mt-0.5 italic">{item.note}</p>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        );
      })}

      <p className="text-[10px] text-slate-400 border-t border-slate-100 pt-4">
        Checklist state is stored locally in this browser. Share a screenshot of the completed checklist for your records.
        No beta invitations should be sent until every required item is checked.
      </p>
    </div>
  );
}
